<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  fallbackIcon: {
    type: String,
    default: 'i-lucide-smile-plus',
  },
});

const emit = defineEmits(['update:modelValue']);

const RESOURCE_ICONS = [
  'i-lucide-package',
  'i-lucide-box',
  'i-lucide-wrench',
  'i-lucide-drill',
  'i-lucide-projector',
  'i-lucide-monitor',
  'i-lucide-speaker',
  'i-lucide-mic',
  'i-lucide-camera',
  'i-lucide-video',
  'i-lucide-lightbulb',
  'i-lucide-lamp-desk',
  'i-lucide-armchair',
  'i-lucide-sofa',
  'i-lucide-chair',
  'i-lucide-utensils',
  'i-lucide-coffee',
  'i-lucide-wine',
  'i-lucide-car',
  'i-lucide-bus',
  'i-lucide-truck',
  'i-lucide-bike',
  'i-lucide-key',
  'i-lucide-shield',
  'i-lucide-badge-check',
  'i-lucide-user',
  'i-lucide-users',
  'i-lucide-user-cog',
  'i-lucide-id-badge',
  'i-lucide-briefcase',
  'i-lucide-headphones',
  'i-lucide-palette',
  'i-lucide-music',
  'i-lucide-tree-pine',
  'i-lucide-tent',
  'i-lucide-map-pin',
  'i-lucide-sparkles',
  'i-lucide-zap',
  'i-lucide-plug',
  'i-lucide-cable',
];

const { t } = useI18n();
const isOpen = ref(false);
const searchTerm = ref('');

const filteredIcons = computed(() => {
  const q = searchTerm.value.trim().toLowerCase();
  if (!q) return RESOURCE_ICONS;
  return RESOURCE_ICONS.filter(icon => icon.toLowerCase().includes(q));
});

const selectIcon = icon => {
  emit('update:modelValue', icon === props.modelValue ? null : icon);
  isOpen.value = false;
  searchTerm.value = '';
};

const clearIcon = () => {
  emit('update:modelValue', null);
  isOpen.value = false;
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) searchTerm.value = '';
};

const displayIcon = computed(
  () => props.modelValue || props.fallbackIcon
);
</script>

<template>
  <div class="relative inline-block">
    <div v-if="isOpen" class="fixed inset-0 z-20" @click="isOpen = false" />
    <button
      type="button"
      class="flex size-12 items-center justify-center rounded-xl border border-n-weak transition-colors"
      :class="
        modelValue
          ? 'bg-n-brand/10 text-n-brand hover:bg-n-brand/20'
          : 'bg-n-alpha-2 text-n-slate-9 hover:bg-n-alpha-3 hover:text-n-slate-11'
      "
      :title="modelValue || t('SETTINGS.RESOURCES.ICON.PICK')"
      @click.stop="toggle"
    >
      <Icon :icon="displayIcon" class="size-6" />
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 top-14 z-30 w-80 rounded-xl border border-n-weak bg-n-solid-2 p-2 shadow-lg"
      @click.stop
    >
      <input
        v-model="searchTerm"
        type="text"
        :placeholder="t('SETTINGS.RESOURCES.ICON.SEARCH_PLACEHOLDER')"
        class="reset-base mb-2 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1.5 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
        autofocus
      />
      <div class="mb-2 flex justify-end">
        <button
          type="button"
          class="text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
          @click="clearIcon"
        >
          {{ t('SETTINGS.RESOURCES.ICON.CLEAR') }}
        </button>
      </div>
      <div class="grid max-h-44 grid-cols-6 gap-1 overflow-y-auto">
        <button
          v-for="icon in filteredIcons"
          :key="icon"
          type="button"
          class="flex size-10 items-center justify-center rounded-md transition-colors"
          :class="
            modelValue === icon
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
          "
          :title="icon.replace('i-lucide-', '')"
          @click="selectIcon(icon)"
        >
          <Icon :icon="icon" class="size-5" />
        </button>
        <p
          v-if="filteredIcons.length === 0"
          class="col-span-6 py-2 text-center text-xs text-n-slate-10"
        >
          {{ t('SETTINGS.RESOURCES.ICON.NO_RESULTS') }}
        </p>
      </div>
    </div>
  </div>
</template>
